import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Button from 'design-system/src/components/Button/Button';
import { FormLabel } from '../FormLabel';
import { Input } from '../Input';
import { DropdownWithPlaceholder } from '../Dropdown.tsx';
import {
  getSpecialtyOptions,
  getEhrOptions,
  getOrganizationTypeOptions,
  getHeardAboutFromOptions,
  createBeforeUnloadHandler,
  getRequiredErrorMessage,
  getSignupType as getSignupTypeFromSizeAndOrgType,
  SignupType,
  specifyOtherHeardAboutFromOptions,
  requiredSpecifyOtherHeardAboutFromOptions,
  isFreeEmailDomain,
} from './utils.tsx';
import { ControllerWrapper } from './ControllerWrapper.tsx';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { SignupErrorMessage } from './SignupErrorMessage';
import { PracticeInfoFormData } from './types';
import { StyledSignupForm } from './styles';
import { PageContainerWithLogo } from './PageContainerWithLogo.tsx';
import ProgressCard from 'design-system/src/components/ProgressCard/ProgressCard.tsx';
import { useMutation } from '@tanstack/react-query';
import { client } from '../../api';
import { handleServerError } from '../../utils';
import { AllFormData, Specialty } from '../../types.ts';
import { createTrackingProps } from 'shared-utils/analytics';
import { submitHubspotForm } from '../../utils/conversionTracking';
import * as Sentry from '@sentry/react';

export type PracticeInfoProps = {
  className?: string;
  signupId: string;
  email?: string;
  onNext: (computedSignupType: SignupType, specialty: Specialty) => void;
  onBeforeUnload?: (formData: PracticeInfoFormData) => void;
  setSignupType: (signupType: SignupType) => void;
  defaultValues?: Partial<PracticeInfoFormData>;
  onFormDataUpdate?: (updates: Partial<PracticeInfoFormData>) => void;
  hubspotBaseData?: Partial<AllFormData>;
  isDemoFlow?: boolean;
};

export const PracticeInfo = ({
  className,
  signupId,
  email,
  onNext,
  onBeforeUnload,
  setSignupType,
  defaultValues,
  onFormDataUpdate,
  hubspotBaseData,
  isDemoFlow,
}: PracticeInfoProps) => {
  const [error, setError] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<PracticeInfoFormData>({
    defaultValues: defaultValues || {},
  });

  const [specialtyValue, ehrValue, heardAboutFromValue] = useWatch({
    control,
    name: ['specialty', 'ehr', 'heard_about_from'],
  });

  const handleBeforeUnload = useMemo(
    () => createBeforeUnloadHandler(onBeforeUnload, getValues),
    [onBeforeUnload, getValues],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  const submitPracticeInfo = useMutation({
    mutationFn: async (newFormData: PracticeInfoFormData) => {
      const response = await client.post('/signup/submit-practice-info', {
        signup_id: signupId,
        size_numeric: newFormData.size_numeric,
        specialty: newFormData.specialty,
        specify_other_specialty: newFormData.specify_other_specialty,
        ehr: newFormData.ehr,
        specify_other_ehr: newFormData.specify_other_ehr,
        organization_type: newFormData.organization_type,
        heard_about_from: newFormData.heard_about_from,
        specify_other_heard_about_from: newFormData.specify_other_heard_about_from,
        practice_website: newFormData.practice_website,
      });
      return response.data;
    },
    onSuccess: async (_, variables) => {
      setError(null);
      onFormDataUpdate?.(variables);
      const computedSignupType = getSignupTypeFromSizeAndOrgType(
        variables.size_numeric,
        variables.organization_type || 'independent',
      );

      try {
        await submitHubspotForm(isDemoFlow ? 'demo' : 'signup', {
          ...hubspotBaseData,
          ...variables,
        });
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        onNext(computedSignupType, variables.specialty);
        setSignupType(computedSignupType);
      }
    },
    onError: (error: any) => {
      console.error('Error submitting practice info:', error);
      const serverErrorMessage = handleServerError(error);
      setError(serverErrorMessage);
    },
  });

  // This wrapper is used to satisfy the handleSubmit input type
  const handleFormSubmit = (newFormData: PracticeInfoFormData) => {
    submitPracticeInfo.mutate(newFormData);
  };

  return (
    <PageContainerWithLogo className={className} data-fs-unmask="true">
      <ProgressCard progressBar={{ current: 3, total: 4 }}>
        <StyledSignupForm onSubmit={handleSubmit(handleFormSubmit)}>
          <Typography styledAs="h5" weight={500} marginBottom="8px">
            Tell us more about your practice
          </Typography>
          <ControllerWrapper>
            <Controller
              control={control}
              name="size_numeric"
              rules={{
                required: { value: true, message: getRequiredErrorMessage('Number of providers') },
                min: { value: 1, message: 'Must have at least 1 provider' },
                max: { value: 10000, message: 'Number of providers must be reasonable' },
              }}
              render={({ field }) => (
                <Input
                  type="number"
                  label="How many providers does your practice have?"
                  hasError={!!errors.size_numeric}
                  placeholder="e.g. 15"
                  min="1"
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
            {errors.size_numeric && <ErrorMessage>{errors.size_numeric.message}</ErrorMessage>}
          </ControllerWrapper>

          <Controller
            name="organization_type"
            control={control}
            rules={{
              required: { value: true, message: getRequiredErrorMessage('Organization type') },
            }}
            render={({ field }) => (
              <ControllerWrapper>
                <FormLabel htmlFor="organization-type">Is your practice part of a larger group?</FormLabel>
                <DropdownWithPlaceholder
                  id="organization-type"
                  $hasError={!!errors.organization_type}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                >
                  {getOrganizationTypeOptions()}
                </DropdownWithPlaceholder>
                {errors.organization_type && <ErrorMessage>{errors.organization_type.message}</ErrorMessage>}
              </ControllerWrapper>
            )}
          />

          {isFreeEmailDomain(email) && (
            <ControllerWrapper>
              <Controller
                control={control}
                name="practice_website"
                rules={{
                  required: { value: true, message: getRequiredErrorMessage('Practice website') },
                }}
                render={({ field }) => (
                  <Input
                    label="Practice website"
                    placeholder="Website, Google listing, or social page"
                    hasError={!!errors.practice_website}
                    {...field}
                    {...createTrackingProps({ unmask: true })}
                  />
                )}
              />
              {errors.practice_website && <ErrorMessage>{errors.practice_website.message}</ErrorMessage>}
            </ControllerWrapper>
          )}

          <Controller
            name="specialty"
            control={control}
            rules={{
              required: { value: true, message: getRequiredErrorMessage('Specialty') },
            }}
            render={({ field }) => (
              <ControllerWrapper>
                <FormLabel htmlFor="specialty">Specialty</FormLabel>
                <DropdownWithPlaceholder
                  id="specialty"
                  $hasError={!!errors.specialty}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                >
                  {getSpecialtyOptions()}
                </DropdownWithPlaceholder>
                {errors.specialty && <ErrorMessage>{errors.specialty.message}</ErrorMessage>}
              </ControllerWrapper>
            )}
          />

          {specialtyValue && specialtyValue.includes('other') && (
            <Controller
              control={control}
              name="specify_other_specialty"
              rules={{ required: { value: true, message: getRequiredErrorMessage('Please specify your specialty') } }}
              render={({ field }) => (
                <Input
                  label="Specify Other Specialty"
                  hasError={!!errors.specify_other_specialty}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
          )}

          {specialtyValue && specialtyValue.includes('other') && errors.specify_other_specialty && (
            <ErrorMessage>{errors.specify_other_specialty.message}</ErrorMessage>
          )}

          <Controller
            name="ehr"
            control={control}
            rules={{
              required: { value: true, message: getRequiredErrorMessage('EHR') },
            }}
            render={({ field }) => (
              <ControllerWrapper>
                <FormLabel htmlFor="ehr">EHR</FormLabel>
                <DropdownWithPlaceholder
                  id="ehr"
                  $hasError={!!errors.ehr}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                >
                  {getEhrOptions()}
                </DropdownWithPlaceholder>
                {errors.ehr && <ErrorMessage>{errors.ehr.message}</ErrorMessage>}
              </ControllerWrapper>
            )}
          />

          {ehrValue === 'other' && (
            <Controller
              control={control}
              name="specify_other_ehr"
              rules={{ required: { value: true, message: getRequiredErrorMessage('Please specify your EHR') } }}
              render={({ field }) => (
                <Input
                  label="Specify Other EHR"
                  hasError={!!errors.specify_other_ehr}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
          )}

          {ehrValue === 'other' && errors.specify_other_ehr && (
            <ErrorMessage>{errors.specify_other_ehr.message}</ErrorMessage>
          )}

          <Controller
            name="heard_about_from"
            control={control}
            rules={{ required: { value: true, message: 'Please select how you heard about us' } }}
            render={({ field }) => (
              <ControllerWrapper>
                <FormLabel htmlFor="heard-about-from">How did you hear about us?</FormLabel>
                <DropdownWithPlaceholder
                  id="heard-about-from"
                  $hasError={!!errors.heard_about_from}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                >
                  {getHeardAboutFromOptions()}
                </DropdownWithPlaceholder>
                {errors.heard_about_from && <ErrorMessage>{errors.heard_about_from.message}</ErrorMessage>}
              </ControllerWrapper>
            )}
          />

          {heardAboutFromValue && specifyOtherHeardAboutFromOptions.includes(heardAboutFromValue) && (
            <Controller
              control={control}
              name="specify_other_heard_about_from"
              rules={{
                required: {
                  value: requiredSpecifyOtherHeardAboutFromOptions.includes(heardAboutFromValue),
                  message: 'Please specify where you heard about us',
                },
              }}
              render={({ field }) => (
                <Input
                  label={
                    requiredSpecifyOtherHeardAboutFromOptions.includes(heardAboutFromValue)
                      ? 'Specify where you heard about us'
                      : 'Specify where you heard about us (optional)'
                  }
                  hasError={!!errors.specify_other_heard_about_from}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
          )}

          {heardAboutFromValue === 'other' && errors.specify_other_heard_about_from && (
            <ErrorMessage>{errors.specify_other_heard_about_from.message}</ErrorMessage>
          )}

          {error && <SignupErrorMessage error={error} />}

          <Button size="lg" width="full" type="submit" isLoading={submitPracticeInfo.isPending}>
            Next
          </Button>
        </StyledSignupForm>
      </ProgressCard>
    </PageContainerWithLogo>
  );
};

export default PracticeInfo;
